/**
 * Fase 8 — hook do streaming: acumula os pedaços, expõe "parar geração" (AbortController) e os
 * créditos cobrados/restantes do evento `done`. Erros antes do início viram `error` (ex.: cota).
 */
import type { AiStreamEvent } from "@iagenix/shared";
import { useCallback, useEffect, useRef, useState } from "react";
import { errorMessage } from "@/lib/api";
import type { StreamHandlers } from "@/lib/ai-api";

export interface StreamDone {
  messageId: string;
  conversationId: string | null;
  credits: number;
  remainingCredits: number;
  interrupted: boolean;
}

export function useAiStream() {
  const [text, setText] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<StreamDone | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const conversationRef = useRef<string | null>(null);

  useEffect(() => () => abortRef.current?.abort(), []);

  /** Roda o stream e devolve o texto acumulado + o `done` (null quando parado ou com erro). */
  const start = useCallback(
    async (
      run: (handlers: StreamHandlers, signal: AbortSignal) => Promise<void>,
    ): Promise<{ text: string; done: StreamDone | null; error: string | null; userMessageId: string | null }> => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setText("");
      setError(null);
      setDone(null);
      setStreaming(true);
      let acc = "";
      let finished: StreamDone | null = null;
      let failed: string | null = null;
      let userMessageId: string | null = null;
      const onEvent = (event: AiStreamEvent) => {
        switch (event.type) {
          case "start":
            conversationRef.current = event.conversationId;
            userMessageId = event.userMessageId;
            break;
          case "delta":
            acc += event.text;
            setText(acc);
            break;
          case "done":
            finished = {
              messageId: event.messageId,
              conversationId: conversationRef.current,
              credits: event.credits,
              remainingCredits: event.remainingCredits,
              interrupted: event.interrupted,
            };
            setDone(finished);
            break;
          case "error":
            failed = event.message;
            setError(event.message);
            break;
        }
      };
      try {
        await run({ onEvent }, controller.signal);
      } catch (err) {
        if (!controller.signal.aborted) {
          failed = errorMessage(err);
          setError(failed);
        }
      } finally {
        if (abortRef.current === controller) abortRef.current = null;
        setStreaming(false);
      }
      return { text: acc, done: finished, error: failed, userMessageId };
    },
    [],
  );

  const stop = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setStreaming(false);
  }, []);

  const reset = useCallback(() => {
    setText("");
    setError(null);
    setDone(null);
  }, []);

  return { text, streaming, error, done, start, stop, reset };
}
